import { FadedText } from '@app/components/base/FadedText';
import { useHoverPopoverClose } from '@app/components/base/HoverPopover';
import { LTime } from '@app/components/date/LTime';
import { getTopicDisplayKeyPoints } from '@app/modules/logger/screens/stream/features/live-topics/helpers/get-topic-display-key-points';
import { getTopicDisplayTitle } from '@app/modules/logger/screens/stream/features/live-topics/helpers/get-topic-display-title';
import { TopicCategoryChip } from '@app/modules/logger/screens/stream/features/live-topics/shared/topic-category-chip/TopicCategoryChip';
import { nlpIntegration } from '@mi-front/api-clients';
import { toDateTimeStr } from '@mi-front/core';
import { DurationForRange, LocaleRelativeTime } from '@mi-front/ui';
import AssistantOutlinedIcon from '@mui/icons-material/AssistantOutlined';
import { Box, Typography } from '@mui/material';
import type { EntitiesDisplayMode } from '../../entities/TopicEntities';
import { TopicEntities } from '../../entities/TopicEntities';
import { AssociatedTopic } from './AssociatedTopic';
import { LiveTopicPopupFooter } from './LiveTopicPopupFooter';

const DESCRIPTION_MAX_HEIGHT_PX = 130;

/**
 * Popup content for a live topic: category chip at top, title, and key points list.
 * Key points block is scrollable when it exceeds max height.
 * Entities: "long" mode shows section titles (Personas, etc.) with chips; "short" mode shows only chips with icon inside each.
 * Footer: divider and two actions (Play from start, Create fragment) via LiveTopicPopupFooter.
 */
export function LiveTopicPopupContent({
  topic,
  entitiesDisplayMode = 'long',
  onClose,
}: {
  topic: nlpIntegration.TopicOutDto;
  entitiesDisplayMode?: EntitiesDisplayMode;
  onClose?: () => void;
}) {
  const closePopover = useHoverPopoverClose();
  const handleClose = onClose ?? closePopover ?? undefined;
  const keyPoints = getTopicDisplayKeyPoints(topic);

  return (
    <Box
      sx={{
        p: 1.25,
        minWidth: 320,
        maxWidth: 480,
        textAlign: 'left',
        position: 'relative',
      }}
    >
      <AssistantOutlinedIcon
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          fontSize: 20,
          color: 'text.disabled',
        }}
      />
      {topic.category && (
        <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1, pr: 4 }}>
          <TopicCategoryChip category={topic.category} />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ '&::first-letter': { textTransform: 'uppercase' } }}
          >
            <LocaleRelativeTime value={topic.start_time} />
            {' · '}
            <LTime value={toDateTimeStr(new Date(topic.start_time))} hour12={false} showSeconds />
            {' · '}
            <DurationForRange from={topic.start_time} to={topic.end_time} />
          </Typography>
        </Box>
      )}
      {topic.associated_to && (
        <AssociatedTopic
          associatedTopicId={topic.associated_to}
          topic={topic}
          onClose={handleClose}
        />
      )}
      <Typography
        variant="subtitle2"
        sx={{
          mb: 0.75,
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
        }}
      >
        {getTopicDisplayTitle(topic)}
      </Typography>
      {keyPoints.length > 0 && (
        <FadedText maxHeight={DESCRIPTION_MAX_HEIGHT_PX}>
          <Box
            component="ul"
            sx={{
              margin: 0,
              pl: 2,
              py: 0,
              lineHeight: 1.5,
              '& li': {
                mb: 0.25,
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
              },
            }}
          >
            {keyPoints.map((point, index) => (
              <Typography key={index} component="li" variant="caption" color="text.secondary">
                {point}
              </Typography>
            ))}
          </Box>
        </FadedText>
      )}

      {topic.entities && (
        <Box sx={{ mt: 1 }}>
          <TopicEntities entities={topic.entities} displayMode={entitiesDisplayMode} />
        </Box>
      )}

      <LiveTopicPopupFooter topic={topic} onClose={handleClose} />
    </Box>
  );
}
